import { setFeatureFlags } from '../utils/feature-flags.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class AdminFeatureFlags extends HTMLElement {
  constructor() {
    super();
    this.currentUser = null;
    this.isAdmin = false;
    this.flags = [];
    this.isLoading = true;
    this.isSaving = false;
  }

  connectedCallback() {
    this.render();
    this.setupAuthListener();
    this.bootstrapAuth();
  }

  async bootstrapAuth() {
    if (!this.isLoading || this.isAdmin) {
      return;
    }
    try {
      if (!window.API) {
        return;
      }
      const authData = await window.API.request('/auth/status');
      if (authData.authenticated && authData.user?.role === 'admin') {
        this.setCurrentUser(authData.user);
      } else if (authData.authenticated) {
        this.setCurrentUser(authData.user);
      } else {
        this.isLoading = false;
        this.renderUnauthorized();
      }
    } catch (error) {
      window.logger.error('Failed to check auth for feature flags:', error);
      this.isLoading = false;
      this.showError('Failed to load feature flags');
    }
  }

  setCurrentUser(user) {
    this.currentUser = user;
    this.isAdmin = user?.role === 'admin';
    if (this.isAdmin) {
      this.loadFlags();
    } else {
      this.isLoading = false;
      this.renderUnauthorized();
    }
  }

  setupAuthListener() {
    this.handleAuthStatusLoaded = (e) => {
      const { user, authenticated } = e.detail;
      if (authenticated && user) {
        this.currentUser = user;
        this.isAdmin = user.role === 'admin';
        if (this.isAdmin) {
          this.loadFlags();
        } else {
          this.renderUnauthorized();
        }
      } else {
        this.renderUnauthorized();
      }
    };

    this.handleAuthSuccess = (e) => {
      const { user } = e.detail;
      if (user) {
        this.currentUser = user;
        this.isAdmin = user.role === 'admin';
        if (this.isAdmin) {
          this.loadFlags();
        } else {
          this.renderUnauthorized();
        }
      }
    };

    document.addEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    document.addEventListener('auth-success', this.handleAuthSuccess);
  }

  async loadFlags() {
    try {
      this.isLoading = true;
      this.render();
      const data = await window.API.request('/admin/feature-flags');
      this.flags = data.flags || [];
    } catch (error) {
      window.logger.error('Failed to load feature flags:', error);
      this.showError('Failed to load feature flags');
    } finally {
      this.isLoading = false;
      this.render();
      this.attachEventListeners();
    }
  }

  render() {
    if (!this.isAdmin && !this.isLoading) {
      return;
    }

    if (this.isLoading) {
      this.innerHTML = `
        <div class="admin-feature-flags">
          <div class="loading-state">
            <div class="spinner"></div>
            <div>Loading feature flags...</div>
          </div>
        </div>
      `;
      return;
    }

    this.innerHTML = `
      <div class="admin-feature-flags">
        <div class="admin-feature-flags-header">
          ${renderPageTitle('🚩', 'Feature Flags')}
          <p>Turn site features on or off without deploying new code</p>
        </div>

        <div class="admin-feature-flags-list">
          ${this.flags.map((flag) => this.renderFlagRow(flag)).join('')}
        </div>

        <div class="admin-feature-flags-footer">
          <div class="save-status" id="save-status"></div>
          <a href="/admin" class="btn btn-secondary" data-route="/admin">← Return to Admin</a>
        </div>
      </div>
    `;
  }

  renderFlagRow(flag) {
    return `
      <div class="feature-flag-row" data-flag-key="${flag.key}">
        <div class="feature-flag-info">
          <h3>${flag.label}</h3>
          <p class="feature-flag-description">${flag.description}</p>
          <code class="feature-flag-key">${flag.key}</code>
        </div>
        <label class="toggle-switch">
          <input type="checkbox"
                 data-flag-key="${flag.key}"
                 ${flag.enabled ? 'checked' : ''}
                 ${this.isSaving ? 'disabled' : ''}>
          <span class="toggle-slider"></span>
        </label>
      </div>
    `;
  }

  renderUnauthorized() {
    this.innerHTML = `
      <div class="admin-feature-flags">
        <div class="unauthorized-state">
          <div class="unauthorized-icon">🚫</div>
          <h2 class="unauthorized-title">Access Denied</h2>
          <p class="unauthorized-message">
            You don't have administrator privileges to access this section.
          </p>
          <a href="/" class="btn btn-primary">Return to Home</a>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    this.addEventListener('change', async (e) => {
      if (e.target.type === 'checkbox' && e.target.dataset.flagKey) {
        await this.handleFlagToggle(e.target);
      }
    });
  }

  async handleFlagToggle(checkbox) {
    if (this.isSaving) return;

    const flagKey = checkbox.dataset.flagKey;
    const enabled = checkbox.checked;
    const previous = this.flags.find((f) => f.key === flagKey);

    try {
      this.isSaving = true;
      this.updateSaveStatus('Saving...', 'saving');

      const data = await window.API.request(`/admin/feature-flags/${flagKey}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled })
      });

      if (data.features) {
        setFeatureFlags(data.features);
        document.dispatchEvent(new CustomEvent('feature-flags-loaded', {
          detail: { features: data.features },
          bubbles: true
        }));
      }

      if (previous) {
        previous.enabled = enabled;
      }

      this.updateSaveStatus('Flag updated!', 'success');
      setTimeout(() => this.updateSaveStatus('', ''), 2000);
    } catch (error) {
      window.logger.error('Failed to update feature flag:', error);
      checkbox.checked = !enabled;
      if (previous) {
        previous.enabled = !enabled;
      }
      this.updateSaveStatus('Failed to save. Please try again.', 'error');
    } finally {
      this.isSaving = false;
    }
  }

  updateSaveStatus(message, type) {
    const statusEl = this.querySelector('#save-status');
    if (statusEl) {
      statusEl.textContent = message;
      statusEl.className = `save-status ${type}`;
    }
  }

  showError(message) {
    this.innerHTML = `
      <div class="admin-feature-flags">
        <div class="error-state">
          <p>${message}</p>
          <a href="/admin" class="btn btn-secondary" data-route="/admin">← Return to Admin</a>
        </div>
      </div>
    `;
  }

  disconnectedCallback() {
    document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    document.removeEventListener('auth-success', this.handleAuthSuccess);
  }
}

customElements.define('admin-feature-flags', AdminFeatureFlags);
